import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SliceState, Statusable } from 'interfaces';
import { StockHistoryInfo } from 'hooks/InventoryHooks/useStockList';
import { deserialize, serialize } from 'class-transformer';
import { toUtcDate } from '@rahtab/system/lib/tools/string';
import { RootState } from 'rahtab-redux';

const initialState: StocksSliceState = {
  loading: 'idle',
  stocks: {},
};

const stockListSlice = createSlice({
  name: 'stocks',
  initialState,
  reducers: {
    setLoading(state, { payload }: PayloadAction<StocksSliceState['loading']>) {
      state.loading = payload;
    },
    addStock(state, { payload }: PayloadAction<StockHistoryInfo>) {
      const {
        StockCode: stockCode,
        StockDate: stockDate,
        StockAmount: stockAmount,
        StockSaleAmount: saleAmount,
        StockFinishReason: stopReason,
        StockSalePercent: salePercent,
        StockFinishDate: stopDate,
        StockPrice: stockPrice,
        Status: statusText,
        StatusCode: status,
        StockMeasureName: unitText,
      } = payload;

      const stockHistoryItem = {
        stockCode,
        stockDate: <Date>(<unknown>serialize(toUtcDate(stockDate))),
        stockAmount,
        saleAmount,
        salePercent,
        stopReason,
        stopDate: <Date>(<unknown>serialize(toUtcDate(stopDate))),
        stockPrice,
        status: parseInt(status, 10) === 1 ? StockStatus.Active : StockStatus.Stopped,
        statusText,
        unitText,
      };
      state.stocks[stockCode] = stockHistoryItem;
    },
  },
});

const { actions, reducer } = stockListSlice;

export const { setLoading, addStock } = actions;

export default reducer;

export function stockListSelector(ids: string): (state: RootState) => Stock;
export function stockListSelector(ids: string[]): (state: RootState) => Array<Stock>;
export function stockListSelector(ids: string | string[]) {
  return (state: RootState) => {
    const { stockList } = state;
    if (typeof ids === 'string') return getStockList(ids);
    return ids.map((id) => getStockList(id));
    function getStockList(id: string): Stock {
      return {
        ...stockList.stocks[id],
        stockDate: deserialize(Date, <string>(<unknown>stockList.stocks[id].stockDate)),
        stopDate: deserialize(Date, <string>(<unknown>stockList.stocks[id].stopDate)),
      };
    }
  };
}

const StockStatus = {
  Stopped: 0,
  Active: 1,
} as const;

export interface Stock extends Statusable<typeof StockStatus> {
  stockCode?: string;
  stockDate: Date;
  stockAmount: string;
  saleAmount: string;
  salePercent: string;
  stopReason: string;
  stopDate: Date;
  stockPrice: string;
  unitText?: string;
}

type StocksSliceState = SliceState<'stocks', Stock>;
